import dayjs from 'dayjs';
import {TrendingFlat} from '@mui/icons-material';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TimedMsgRequestTimesCard, {
  iTimedMsgTimesItem,
} from './TimedMsgRequestTimesCard';
import IconWarning from '../../../../../asset/icons/icon_warning.svg';
import IconChecked from '../../../../../asset/icons/icon_checked.svg';
import {ITimedDuplicateTimes, iTimedMsgDateRange} from '../TimedMessageRequest';
import {isAllDaysTimed} from '../../../../../components/utlis/date-time/GetDaysInDateRange';
import {companyIdSelector} from '../../../../../store/Slices/companySlice';
import {useStoreSelector} from '../../../../../store/configstore';
import {useRqTimedMsgDateTimeExistence} from '../../../../../react_query/messages/timed-messages/TimedMsgDateTimeExistence';
import {IStation} from '../../../../../dto/Station.dto';
import TimedMsgRequestReviewDayTimes from '../review/TimedMsgRequestReviewDayTimes';

type Props = {
  isAddSlot: boolean;
  items: iTimedMsgTimesItem[];
  selectedWeekdays: string[];
  enableWeekdays: boolean;
  setDaysTimes: React.Dispatch<React.SetStateAction<iTimedMsgTimesItem[]>>;
  availableDays: string[];
  duplicateTimes?: ITimedDuplicateTimes[];
};

const TimedMsgRequestTimes = ({
  isAddSlot,
  items,
  selectedWeekdays,
  enableWeekdays,
  setDaysTimes,
  availableDays,
  duplicateTimes,
}: Props) => {
  const handleRemove = useCallback(
    (id: number) => {
      setDaysTimes(prevItems => {
        const index = prevItems.findIndex(o => o.id === id);
        if (index === -1) return prevItems;
        const newItems = [...prevItems];
        newItems.splice(index, 1);
        return newItems;
      });
    },
    [setDaysTimes],
  );

  const handleAddSlot = useCallback(() => {
    setDaysTimes(prevItems => [
      ...prevItems,
      {
        id: Date.now(),
        selectedDays: [],
        timeRanges: [],
      },
    ]);
  }, [setDaysTimes]);

  return (
    <>
      <Box className='flex justify-between items-start'>
        <Box>
          <Typography variant='h5' className='mb-2'>
            Select Time
          </Typography>
          <Typography
            variant='subtitle1'
            className='text-white-50 text-14 mb-8 italic'></Typography>
        </Box>
        {isAddSlot && (
          <Button
            startIcon={<AddIcon />}
            variant='outlined'
            size='small'
            onClick={handleAddSlot}>
            Add Slot
          </Button>
        )}
      </Box>

      <Stack
        direction='row'
        spacing={2}
        className='pr-2 mt-6 w-880 h-390 overflow-auto scrollbar'>
        {items.map((item, index) => (
          <TimedMsgRequestTimesCard
            key={item.id}
            title={enableWeekdays ? `Slot ${index + 1}` : ``}
            item={item}
            enableWeekdays={enableWeekdays}
            selectedWeekdays={selectedWeekdays}
            removeBtnDisabled={items.length === 1}
            setDaysTimes={setDaysTimes}
            onRemove={handleRemove}
            availableDays={availableDays}
            duplicateTimes={duplicateTimes}
          />
        ))}
      </Stack>
    </>
  );
};

export default TimedMsgRequestTimes;

export const MultipleDateSelectTimeCmpt = ({
  dateRanges,
  setDateRanges,
  selectedStations,
  updateId,
  defaultDateRanges,
}: {
  dateRanges: iTimedMsgDateRange[];
  setDateRanges: React.Dispatch<React.SetStateAction<iTimedMsgDateRange[]>>;
  selectedStations: IStation[];
  updateId?: number;
  defaultDateRanges: iTimedMsgDateRange[];
}) => {
  const [duplicateTimes, setDuplicateTimes] = useState<ITimedDuplicateTimes[]>(
    [],
  );
  const companyId = useStoreSelector(companyIdSelector);
  const [page, setPage] = useState(1);
  const [daysTimes, setDaysTimes] = useState<iTimedMsgTimesItem[]>([]);
  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{
    start_date: dayjs.Dayjs | null;
    end_date: dayjs.Dayjs | null;
  }>({start_date: null, end_date: null});
  const [selectedWeekdays, setSelectedWeekdays] = useState<string[]>([]);
  const [dateRangeIds, setDateRangeIds] = useState<number>(0);
  const [currentDateRange, setCurrentDateRange] =
    useState<iTimedMsgDateRange | null>(null);

  const isAllDaysSelected = useMemo(
    () => (currentDateRange ? isAllDaysTimed(currentDateRange) : false),
    [currentDateRange],
  );

  const previousDaysTimes = useMemo(() => {
    if (updateId) {
      const matchedRange = defaultDateRanges.find(
        val => val.id === dateRangeIds,
      );
      return matchedRange ? matchedRange.day_times : [];
    }
    return [];
  }, [updateId, dateRangeIds, defaultDateRanges]);

  const timeValidatePayload = {
    type: 'Active',
    day_type: 'multiple',
    dates: dateRange
      ? [
          {
            start_date: dateRange.start_date?.format('YYYY-MM-DD'),
            end_date: dateRange.end_date?.format('YYYY-MM-DD'),
          },
        ]
      : [],
    day_times: daysTimes.map(o => ({
      days: o.selectedDays.map(d => d.toLowerCase()),
      times: o.timeRanges.map(t => t.start_time?.format('HH:mm:ss')),
    })),
    station_ids: selectedStations.map(val => val.id),
    ...(updateId && {timed_id: updateId}),
  };

  const {data, isFetching, isError} = useRqTimedMsgDateTimeExistence(
    companyId,
    timeValidatePayload,
    timeValidatePayload.day_times.some(
      val => val.days.length > 0 && val.times.length > 0,
    ),
  );

  useEffect(() => {
    if (data && data.length && !isFetching && !isError) {
      setDuplicateTimes(
        data.map(o => {
          return {
            days: JSON.parse(o.week_days),
            times: JSON.parse(o.start_times),
          };
        }),
      );
      setDateRanges(prev => {
        return prev.map((val, index) => {
          if (index === page - 1) {
            return {...val, isError: true};
          }
          return val;
        });
      });
    } else {
      setDuplicateTimes([]);
      setDateRanges(prev => {
        return prev.map((val, index) => {
          if (index === page - 1) {
            return {...val, isError: false};
          }
          return val;
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isError, isFetching, setDateRanges]);

  useEffect(() => {
    const flattenedWeeks = daysTimes.map(o => o.selectedDays).flat();
    const uniqueWeeks = flattenedWeeks.filter(
      (item, index, array) => array.indexOf(item) === index,
    );
    setSelectedWeekdays([...uniqueWeeks]);
  }, [daysTimes]);

  const handleRemove = useCallback(
    (id: number) => {
      setDaysTimes(prevItems => {
        const index = prevItems.findIndex(o => o.id === id);
        if (index === -1) return prevItems;
        const newItems = [...prevItems];
        newItems.splice(index, 1);
        return newItems;
      });
    },
    [setDaysTimes],
  );

  const handleChange = useCallback(
    (_event: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
      setDaysTimes([]);
      setDateRange({start_date: null, end_date: null});
    },
    [],
  );

  const handleAddSlot = useCallback(() => {
    setDaysTimes(prevItems => {
      return [
        ...prevItems,
        {
          id: Date.now(),
          availableDays: prevItems[0]
            ? (prevItems[0].availableDays as string[])
            : [],
          selectedDays: [],
          timeRanges: [],
        },
      ];
    });
  }, [setDaysTimes]);

  // When page changes, always initialize daysTimes from dateRanges[page-1].day_times
  useEffect(() => {
    setDateRanges(prev => {
      return prev.map((val, index) => {
        if (index === page - 1) {
          setDateRangeIds(val.id);
          if (daysTimes.length === 0) {
            setDaysTimes(val.day_times ?? []);
            setAvailableDays(val.chosenDays ?? []);
          }
          setDateRange({start_date: val.start_date, end_date: val.end_date});
          setCurrentDateRange(val);
        }
        return val;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDateRanges, page]);

  // When daysTimes changes, always update dateRanges[page-1].day_times
  useEffect(() => {
    setDateRanges(prev => {
      return prev.map((val, index) => {
        if (index === page - 1 && daysTimes.length) {
          const newVal = {...val, day_times: daysTimes};
          setCurrentDateRange(newVal);
          return newVal;
        }
        return val;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysTimes]);

  return (
    <Grid container spacing={1}>
      <Grid item md={updateId ? 9 : 12}>
        <Stack direction='row' spacing={1}>
          <Box
            className='w-430 pr-3 border-right-1'
            sx={{position: 'relative'}}>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='h5'>Select Time</Typography>
              {!isAllDaysSelected ? (
                <Button
                  sx={{position: 'absolute', bottom: '-20px', right: '30px'}}
                  startIcon={<AddIcon />}
                  variant='outlined'
                  size='small'
                  onClick={handleAddSlot}>
                  Add Slot
                </Button>
              ) : null}
            </Stack>
            <Typography
              variant='subtitle1'
              className='text-white-50 text-14 mb-1 italic'>
              Please choose the time when the message needs to play
            </Typography>
            <Stack
              className='font-bold'
              direction='row'
              spacing={1}
              sx={{position: 'absolute', bottom: '-20px'}}>
              {dateRange.start_date?.format('MM/DD/YYYY')} <TrendingFlat />{' '}
              {dateRange.end_date?.format('MM/DD/YYYY')}
            </Stack>
          </Box>
          <List className='overflow-auto scrollbar w-300 h-100'>
            {dateRanges.map(val => (
              <ListItem key={val.id} disableGutters className='py-1'>
                <ListItemText
                  primaryTypographyProps={{className: 'text-14 pl-2'}}
                  primary={
                    <Stack
                      className='font-bold'
                      direction='row'
                      spacing={1}
                      alignContent='bottom'>
                      {val.start_date?.format('MM/DD/YYYY')} <TrendingFlat />{' '}
                      {val.end_date?.format('MM/DD/YYYY')}
                    </Stack>
                  }
                />
                {val.day_times &&
                (val.day_times.some(
                  val =>
                    val.selectedDays.length === 0 ||
                    val.timeRanges.length === 0,
                ) ||
                  val.isError ||
                  !isAllDaysTimed(val)) ? (
                  <img
                    src={IconWarning}
                    alt='Attention'
                    className='w-21 mr-2'
                    title='Empty Form'
                  />
                ) : (
                  <img
                    src={IconChecked}
                    alt='Attention'
                    className='w-21 mr-2'
                    title='Empty Form'
                  />
                )}
              </ListItem>
            ))}
          </List>
        </Stack>

        <Stack spacing={2}>
          <Stack
            direction='row'
            spacing={2}
            className='pr-2 mt-6 w-880 h-350 overflow-auto scrollbar'>
            {daysTimes &&
              daysTimes.map((item, index) => (
                <TimedMsgRequestTimesCard
                  key={item.id}
                  title={`Slot ${index + 1}`}
                  item={item}
                  enableWeekdays
                  selectedWeekdays={selectedWeekdays}
                  removeBtnDisabled={Boolean(
                    daysTimes && daysTimes.length === 1,
                  )}
                  setDaysTimes={setDaysTimes}
                  onRemove={handleRemove}
                  availableDays={availableDays}
                  duplicateTimes={duplicateTimes}
                />
              ))}
          </Stack>
          <Pagination
            count={dateRanges.length}
            page={page}
            onChange={handleChange}
          />
        </Stack>
      </Grid>

      {updateId && previousDaysTimes ? (
        <Grid item md={3}>
          <Typography className='text-20 font-bold' align='center'>
            Existing Time
          </Typography>
          <TimedMsgRequestReviewDayTimes items={previousDaysTimes} />
        </Grid>
      ) : null}
    </Grid>
  );
};
